// LeadPipelineBoard.jsx - Kanban-style board for managing leads
// 6 columns: New | To Call | Calling | Callback | Follow Up | Booked
// Real-time updates, smart queue, quick actions

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
  hot:"#dc2626", warm:"#f59e0b", cold:"#3b82f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

const COLUMNS = [
  { id: "new",                  title: "🆕 New",         color: C.gold,    desc: "Just arrived" },
  { id: "bot_chatting",         title: "🤖 Bot Chatting", color: C.purple,  desc: "Bot is engaging" },
  { id: "to_call",              title: "📞 To Call",      color: C.blue,    desc: "Time to call!" },
  { id: "calling",              title: "☎️ Calling",       color: C.warm,    desc: "On the phone" },
  { id: "callback_scheduled",   title: "⏰ Callback",      color: "#a855f7", desc: "Scheduled call" },
  { id: "follow_up",            title: "🔄 Follow Up",    color: C.dim,     desc: "Long-term nurture" },
  { id: "booked",               title: "✅ Booked",        color: C.green,   desc: "Won!" },
  { id: "lost",                 title: "❌ Lost",          color: C.red,     desc: "No interest" },
];

// ★ Lead kinds (owner 2026-10-16) — so the desk calls fresh leads first.
//   "New" = a real (non cold-list) lead that came in, or re-submitted a form,
//   in the last 72h. lead-webhook stamps created_at on re-engagement too.
const NEW_LEAD_HOURS = 72;
const isColdList = (l) => l.source === "cold_outreach";
const isNewLead = (l) => !isColdList(l) && l.created_at && (Date.now() - new Date(l.created_at).getTime()) < NEW_LEAD_HOURS * 3600000;
// 0 new & never called · 1 new, didn't answer (call back) · 2 other real lead · 3 cold list
const leadKindRank = (l) => isColdList(l) ? 3 : isNewLead(l) ? ((l.call_attempts || 0) === 0 ? 0 : 1) : 2;

export default function LeadPipelineBoard() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("active"); // 'active' | 'all' | 'today'

  // Debounce real-time refreshes. A burst of inserts (e.g. when a GHL backlog
  // releases 200 leads in 5 seconds) used to fire `load()` 200 times and lock
  // the browser. Now we coalesce them into one refresh every 1.5 seconds.
  const refreshTimerRef = useRef(null);
  const scheduleRefresh = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      load();
    }, 1500);
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel("pipeline-board")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, scheduleRefresh)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    };
  }, [filter]);

  const load = async () => {
    // Sort by the MORE RECENT of (pipeline_status_changed_at, created_at) so
    // brand-new leads (which may not yet have pipeline_status_changed_at set)
    // always appear at the top — not buried under 12-day-old leads that had
    // their status touched once.
    let q = supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });   // freshest first, always

    if (filter === "active") {
      q = q.not("pipeline_status", "in", '("booked","lost")');
    } else if (filter === "uncalled") {
      // Only active leads that have never been called by the receptionist.
      q = q.not("pipeline_status", "in", '("booked","lost","bot_chatting")')
           .or("call_attempts.is.null,call_attempts.eq.0");
    } else if (filter === "today") {
      // ★ Real leads only. The cold-outreach cron adds ~100 old contacts a day
      //   with today's created_at; mixed in, they buried the 2-3 real new
      //   Facebook leads (owner 2026-10-16). Cold list has its own filter.
      const today = new Date(); today.setHours(0,0,0,0);
      q = q.gte("created_at", today.toISOString()).neq("source", "cold_outreach");
    } else if (filter === "cold") {
      q = q.eq("source", "cold_outreach").not("pipeline_status", "in", '("booked","lost")');
    }

    // Bump to 500 so the freshest day's intake (plus today's still-active
    // older leads) all fit on screen.
    q = q.limit(500);

    const { data } = await q;
    setLeads(data || []);
  };

  const filtered = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.full_name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.phone?.includes(q) || l.interest?.toLowerCase().includes(q);
  });

  // Group by pipeline_status
  const groups = {};
  COLUMNS.forEach(c => groups[c.id] = []);
  filtered.forEach(l => {
    const col = l.pipeline_status || "new";
    if (groups[col]) groups[col].push(l);
  });

  // Smart call queue: hot leads first, fewer attempts first (give everyone a chance),
  // then oldest waiting (don't leave anyone hanging). Callback queue: by scheduled time.
  const tempRank = { hot: 0, warm: 1, cold: 2 };
  if (groups.to_call) {
    groups.to_call.sort((a, b) => {
      // 0. ★ New leads before everything (owner 2026-10-16): brand-new and not
      //    yet called → new leads who didn't answer (call back) → other real
      //    leads → the cold-outreach list last. The desk was calling cold
      //    list contacts first while fresh Facebook leads waited.
      const kA = leadKindRank(a), kB = leadKindRank(b);
      if (kA !== kB) return kA - kB;
      // 1. Hot temperature first (so high-intent leads get called before cold ones)
      const tA = tempRank[a.lead_temperature] ?? 3;
      const tB = tempRank[b.lead_temperature] ?? 3;
      if (tA !== tB) return tA - tB;
      // 2. Untouched leads first (call_attempts ascending)
      const aA = a.call_attempts || 0;
      const aB = b.call_attempts || 0;
      if (aA !== aB) return aA - aB;
      // 3. NEWEST first (within same temperature + attempts). Was previously
      //    oldest-first which caused fresh leads like Coley/Yolie (a few hours
      //    old) to get buried under 12-day-old untouched leads. Newer leads
      //    are highest-intent — strike while the iron is hot.
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }
  if (groups.callback_scheduled) {
    groups.callback_scheduled.sort((a, b) => new Date(a.callback_scheduled_at || 0) - new Date(b.callback_scheduled_at || 0));
  }

  // Stats
  const todayBooked = leads.filter(l => {
    if (l.pipeline_status !== "booked") return false;
    const updated = new Date(l.pipeline_status_changed_at);
    const today = new Date(); today.setHours(0,0,0,0);
    return updated >= today;
  }).length;

  const todayNew = leads.filter(l => {
    if (isColdList(l)) return false;
    const created = new Date(l.created_at);
    const today = new Date(); today.setHours(0,0,0,0);
    return created >= today;
  }).length;

  const toCallCount = (groups.to_call || []).length + (groups.callback_scheduled || []).filter(l => new Date(l.callback_scheduled_at) <= new Date(Date.now() + 15*60000)).length;
  const hotCount = filtered.filter(l => l.lead_temperature === "hot" && !["booked","lost"].includes(l.pipeline_status)).length;

  // ★ The number the owner asked for: active leads the receptionist has NOT
  //   called yet (call_attempts === 0). Excludes booked/lost and bot_chatting
  //   (the bot is handling those over SMS — no human call owed yet).
  const needsCallCount = filtered.filter(l =>
    !isColdList(l) &&
    !["booked","lost","bot_chatting"].includes(l.pipeline_status) &&
    (l.call_attempts || 0) === 0
  ).length;

  // ★ Interested replies the bot/staff hasn't answered yet (flagged server-side
  //   by the unanswered-replies-scan cron). The "nothing falls through" number.
  const repliesWaiting = filtered.filter(l =>
    l.needs_human_reply && !["booked","lost"].includes(l.pipeline_status)
  ).length;

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:18}}>
        <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>📊 Lead Pipeline Board</div>
        <div style={{fontSize:12,color:C.muted}}>Live view of every lead - drag to update or click for details</div>
      </div>

      {/* Stats bar */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:14}}>
        <Stat label="💬 Replies Waiting" value={repliesWaiting} color={repliesWaiting > 0 ? C.red : C.green}/>
        <Stat label="📞 Needs 1st Call" value={needsCallCount} color={needsCallCount > 0 ? C.red : C.green}/>
        <Stat label="Today's New" value={todayNew} color={C.gold}/>
        <Stat label="🔥 Hot Now" value={hotCount} color={C.hot}/>
        <Stat label="✅ Booked Today" value={todayBooked} color={C.green}/>
        <Stat label="Pipeline Value" value={"$" + filtered.filter(l => !["booked","lost"].includes(l.pipeline_status)).reduce((s,l) => s + (+l.estimated_value||0), 0).toFixed(0)} color={C.purple}/>
      </div>

      {/* Search + filter */}
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search name, phone, interest..." style={{...inp, flex:1, minWidth:240}}/>
        <select value={filter} onChange={e=>setFilter(e.target.value)} style={{...inp, width:"auto"}}>
          <option value="active">Active only (no booked/lost)</option>
          <option value="uncalled">📞 Needs first call</option>
          <option value="today">🆕 Today's new leads</option>
          <option value="cold">❄️ Cold list only</option>
          <option value="all">All recent (500)</option>
        </select>
      </div>

      {/* Board - horizontal scroll on mobile */}
      <div style={{display:"flex",gap:10,overflowX:"auto",paddingBottom:14}}>
        {COLUMNS.map(col => (
          <Column key={col.id} col={col} leads={groups[col.id] || []} onCardClick={setSelectedLead} />
        ))}
      </div>

      {selectedLead && (
        <LeadDetailPanel
          leadId={selectedLead.id}
          onClose={() => { setSelectedLead(null); load(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
function Column({ col, leads, onCardClick }) {
  return (
    <div style={{
      flex: "0 0 280px",
      background: C.light,
      borderRadius: 10,
      borderTop: "3px solid " + col.color,
      display: "flex",
      flexDirection: "column",
      maxHeight: 700,
    }}>
      <div style={{padding:"10px 14px",borderBottom:"1px solid "+C.border}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:13,fontWeight:700,color:col.color}}>{col.title}</div>
          <span style={{padding:"2px 8px",background:col.color,color:"#fff",borderRadius:50,fontSize:10,fontWeight:700}}>{leads.length}</span>
        </div>
        <div style={{fontSize:10,color:C.muted}}>{col.desc}</div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"8px",display:"flex",flexDirection:"column",gap:6}}>
        {leads.length === 0 && (
          <div style={{textAlign:"center",color:C.dim,fontSize:11,padding:20}}>—</div>
        )}
        {leads.map(l => <LeadCard key={l.id} lead={l} onClick={() => onCardClick(l)} />)}
      </div>
    </div>
  );
}

function LeadCard({ lead, onClick }) {
  const tempColor = lead.lead_temperature === "hot" ? C.hot : lead.lead_temperature === "cold" ? C.cold : C.warm;
  const tempIcon = lead.lead_temperature === "hot" ? "🔥" : lead.lead_temperature === "cold" ? "❄️" : "✨";

  // "X ago" helper — minutes/hours/days based on how long since the timestamp
  const ago = (ts) => {
    if (!ts) return null;
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 0) return null;
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins/60)}h`;
    return `${Math.floor(mins/1440)}d`;
  };

  const createdAgo = ago(lead.created_at);
  // "Last touch" = the most recent of: contact, call, reply, status change
  const lastTouchTs = [
    lead.last_contacted_at,
    lead.last_call_at,
    lead.last_reply_at,
    lead.last_touched_at,
    lead.last_message_at,
  ].filter(Boolean).map(t => new Date(t).getTime()).sort((a,b) => b-a)[0];
  const lastTouchAgo = lastTouchTs ? ago(new Date(lastTouchTs).toISOString()) : null;
  const lastTouchMins = lastTouchTs ? Math.floor((Date.now() - lastTouchTs) / 60000) : null;

  // Visual signal: green if touched in last 24h, orange if 1-7 days, red if 7+ days or never
  const touchColor = lastTouchMins === null ? C.red
    : lastTouchMins < 1440 ? C.green
    : lastTouchMins < 7*1440 ? C.warm
    : C.red;

  const minsSinceCreated = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 60000);
  const isFresh = minsSinceCreated < 30;

  // ── Call status (one receptionist calls every lead — the owner wants to see
  //    at a glance who's been called and who's still waiting). call_attempts /
  //    last_call_at are stamped by the "Called —" buttons in the detail panel.
  const callAttempts = lead.call_attempts || 0;
  const isBotHandled = lead.pipeline_status === "bot_chatting";
  const isClosed = ["booked","lost"].includes(lead.pipeline_status);
  const needsCall = callAttempts === 0 && !isBotHandled && !isClosed;
  const calledAgo = ago(lead.last_call_at);
  // Card left edge: red = waiting for first call, green = already called,
  // purple = bot is handling it, grey = closed/other.
  const callEdge = needsCall ? C.red : callAttempts > 0 ? C.green : isBotHandled ? C.purple : C.border;

  // Callback specific
  const isCallbackDue = lead.pipeline_status === "callback_scheduled" && lead.callback_scheduled_at && new Date(lead.callback_scheduled_at) <= new Date(Date.now() + 15*60000);
  const callbackTime = lead.callback_scheduled_at ? new Date(lead.callback_scheduled_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : null;

  return (
    <div onClick={onClick} style={{
      padding: "9px 10px",
      background: isCallbackDue ? "rgba(168,85,247,0.1)" : isFresh ? "rgba(180,140,60,0.06)" : C.surface,
      border: "1px solid " + (isCallbackDue ? "#a855f7" : C.border),
      borderLeft: "4px solid " + callEdge,
      borderRadius: 8,
      cursor: "pointer",
      transition: "all 0.15s",
    }}
    onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"}
    onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6,marginBottom:4}}>
        <div style={{display:"flex",alignItems:"center",gap:4,minWidth:0}}>
          <span>{tempIcon}</span>
          <span style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.full_name || "No name"}</span>
        </div>
        {isCallbackDue && <span style={{fontSize:9,padding:"1px 5px",background:"#a855f7",color:"#fff",borderRadius:50,fontWeight:700}}>{callbackTime}</span>}
      </div>

      {/* ★ Lead-kind tag (owner 2026-10-16): fresh leads stand out, cold list is
          clearly marked, so the desk always knows who to call first. */}
      {(() => {
        const rank = leadKindRank(lead);
        const tag = rank === 0 ? { t: "🆕 NEW LEAD — CALL FIRST", bg: C.green, fg: "#fff" }
          : rank === 1 ? { t: `🔁 NEW · CALL BACK (${callAttempts}×)`, bg: "#d97706", fg: "#fff" }
          : rank === 3 ? { t: "❄️ COLD LIST", bg: "#e5e7eb", fg: "#6b7280" }
          : null;
        if (!tag || isClosed) return null;
        return (
          <div style={{display:"inline-block",marginBottom:5,padding:"2px 8px",background:tag.bg,color:tag.fg,borderRadius:5,fontSize:10,fontWeight:800,letterSpacing:"0.02em"}}>
            {tag.t}{rank <= 1 && createdAgo ? ` · ${createdAgo} ago` : ""}
          </div>
        );
      })()}

      {/* ★ REPLY WAITING — an interested SMS reply nobody has answered yet */}
      {lead.needs_human_reply && (
        <div style={{display:"block",marginBottom:5}}>
          <div style={{display:"inline-block",padding:"2px 8px",background:C.red,border:"1px solid "+C.red,borderRadius:5,fontSize:10,fontWeight:800,color:"#fff",letterSpacing:"0.02em"}}>
            💬 REPLY WAITING — RESPOND
          </div>
          {lead.last_inbound_text && (
            <div style={{marginTop:4,padding:"4px 7px",background:C.redL,border:"1px solid "+C.red+"33",borderRadius:5,fontSize:10,color:C.red,fontStyle:"italic",lineHeight:1.3}}>
              "{lead.last_inbound_text.length > 70 ? lead.last_inbound_text.slice(0,70)+"…" : lead.last_inbound_text}"
            </div>
          )}
        </div>
      )}

      {/* ★ CALL STATUS — the thing the owner wants to see instantly */}
      {needsCall && !lead.needs_human_reply && (
        <div style={{display:"inline-block",marginBottom:5,padding:"2px 8px",background:C.redL,border:"1px solid "+C.red+"55",borderRadius:5,fontSize:10,fontWeight:800,color:C.red,letterSpacing:"0.02em"}}>
          📞 NOT CALLED YET
        </div>
      )}
      {callAttempts > 0 && !isClosed && (
        <div style={{display:"inline-block",marginBottom:5,padding:"2px 8px",background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:5,fontSize:10,fontWeight:700,color:C.green}}>
          📞 Called{calledAgo ? ` ${calledAgo} ago` : ""} · {callAttempts} {callAttempts > 1 ? "tries" : "try"}
        </div>
      )}
      {isBotHandled && callAttempts === 0 && (
        <div style={{display:"inline-block",marginBottom:5,padding:"2px 8px",background:"rgba(124,58,237,0.10)",border:"1px solid rgba(124,58,237,0.3)",borderRadius:5,fontSize:10,fontWeight:700,color:C.purple}}>
          🤖 Bot chatting — no call needed yet
        </div>
      )}

      <div style={{fontSize:11,color:C.muted,marginBottom:4}}>
        {lead.interest && <span>{lead.interest}</span>}
        {lead.phone && <span> · {lead.phone}</span>}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10}}>
        <div style={{display:"flex",alignItems:"center",gap:5}}>
          <span style={{color:tempColor,fontWeight:700}}>Score {lead.lead_score || 50}</span>
          {lead.estimated_value > 0 && <span style={{color:C.muted}}>·</span>}
          {lead.estimated_value > 0 && <span style={{color:C.green,fontWeight:600}}>${lead.estimated_value}</span>}
        </div>
        <span style={{color:C.dim}} title={`Created ${new Date(lead.created_at).toLocaleString()}`}>
          📅 {createdAgo}
        </span>
      </div>
      <div style={{marginTop:4,display:"flex",justifyContent:"flex-end",alignItems:"center",fontSize:10,color:C.muted}}>
        <span style={{color:touchColor,fontWeight:600}} title={lastTouchTs ? `Last contacted ${new Date(lastTouchTs).toLocaleString()}` : "No contact yet"}>
          {lastTouchAgo ? `💬 Last touch ${lastTouchAgo} ago` : "⚠️ No SMS/contact yet"}
        </span>
      </div>
      {/* Stale warning: card hasn't been touched in 3+ days (still active in pipeline) */}
      {lastTouchMins !== null && lastTouchMins > 3*1440 && !["booked","lost"].includes(lead.pipeline_status) && (
        <div style={{marginTop:4,padding:"3px 6px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.2)",borderRadius:5,fontSize:9,color:C.red,fontWeight:700,textAlign:"center"}}>
          ⚠️ STALE — no activity {Math.floor(lastTouchMins/1440)}d
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// LEAD DETAIL PANEL - opens on card click with full details + actions
// ────────────────────────────────────────────────────────
function LeadDetailPanel({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [showCallback, setShowCallback] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [showQuickSMS, setShowQuickSMS] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  useEffect(() => { load(); }, [leadId]);

  const load = async () => {
    const { data: l } = await supabase.from("leads").select("*").eq("id", leadId).single();
    setLead(l);
    const { data: acts } = await supabase.from("lead_activities").select("*").eq("lead_id", leadId).order("created_at", { ascending: false }).limit(50);
    setActivities(acts || []);
    const { data: tpls } = await supabase.from("lead_sms_quick_templates").select("*").eq("is_active", true).order("display_order");
    setSmsTemplates(tpls || []);
  };

  const updateStatus = async (newStatus, extras = {}) => {
    setLoading(true);
    await supabase.from("leads").update({ pipeline_status: newStatus, ...extras }).eq("id", leadId);
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "status_changed",
      channel: "system",
      direction: "system",
      content: `Status: ${lead.pipeline_status} → ${newStatus}`,
    });
    setLoading(false);
    await load();
  };

  const startCalling = async () => {
    setLoading(true);
    await supabase.from("leads").update({
      pipeline_status: "calling",
      bot_paused: true,                    // ★ stop bot when human takes over
      call_attempts: (lead.call_attempts || 0) + 1,
      last_call_at: new Date().toISOString(),
    }).eq("id", leadId);
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "call_started",
      channel: "phone",
      direction: "outbound",
      content: "Started call",
    });
    setLoading(false);
    await load();
  };

  // ★ Standalone "I called" — records the call WITHOUT forcing a final outcome.
  //   For the common case: you phoned them, reached them (or left a voicemail),
  //   and you'll pick Booked / Callback / Lost next — or just want it logged so
  //   the card turns green. Keeps the lead in its current column, increments the
  //   attempt count, stamps last_call_at, and pauses the bot so it won't text
  //   over you. (The "Called — Booked/No Answer" buttons below also record a
  //   call; this is for when neither of those is the outcome yet.)
  const markCalled = async () => {
    setLoading(true);
    const nextAttempt = (lead.call_attempts || 0) + 1;
    await supabase.from("leads").update({
      bot_paused: true,
      call_attempts: nextAttempt,
      last_call_at: new Date().toISOString(),
    }).eq("id", leadId);
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "call_started",
      channel: "phone",
      direction: "outbound",
      content: `Call logged (attempt ${nextAttempt})`,
    });
    setLoading(false);
    await load();
  };

  const markBooked = async () => {
    await supabase.from("leads").update({
      pipeline_status: "booked",
      booked: true,
      status: "booked",
    }).eq("id", leadId);
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "booked",
      channel: "system",
      content: "Marked as booked!",
    });
    await load();
    setTimeout(onClose, 500);
  };

  // ★ NO-ANSWER AUTO-TEXT (owner request 2026-08-11): the moment the front desk
  //   clicks "No Answer", Sophia texts the lead a CAMPAIGN-SPECIFIC message so
  //   the missed call still converts. sender:"ai" logs it as Sophia's own
  //   message, so when the lead replies she knows exactly what it's about and
  //   books them end-to-end (creating the client + calendar appointment).
  //   The call-back cadence below KEEPS running — it only stops when the lead
  //   is booked (Sophia marks pipeline_status='booked' → out of the queue).
  const sendNoAnswerText = async () => {
    try {
      if (!lead.phone) return;
      if (lead.opted_out) return; // never text an opt-out
      // Double-send guard: one auto-text per 12h max.
      if (lead.no_answer_sms_at && (Date.now() - new Date(lead.no_answer_sms_at).getTime()) < 12 * 3600 * 1000) return;

      const first = (lead.first_name || (lead.full_name || "").split(/\s+/)[0] || "there").trim() || "there";
      const interest = String(lead.interest || lead.interest_detail || "").trim();
      // Campaign-specific wording — mirrors what Sophia quotes when they reply.
      let msg;
      if (/botox/i.test(interest)) {
        msg = `Hi ${first}, it's Sophia from Nova Med Spa 💛 We just tried to call you about the $9.99/unit Botox special you asked about! Want me to grab you a spot? Just reply with a day & time that works 😊 (Reply STOP to opt out)`;
      } else if (interest) {
        msg = `Hi ${first}, it's Sophia from Nova Med Spa 💛 We just tried to call you about ${interest}! Want me to get you booked? Just reply with a day & time that works 😊 (Reply STOP to opt out)`;
      } else {
        msg = `Hi ${first}, it's Sophia from Nova Med Spa 💛 We just tried to reach you by phone! Want me to get you booked? Just reply with a day & time that works 😊 (Reply STOP to opt out)`;
      }

      const { data: res, error } = await supabase.functions.invoke("send-sms", {
        body: { to: lead.phone, message: msg, sender: "ai", source: "lead_no_answer_followup" },
      });
      if (error || res?.ok === false) {
        console.warn("[no-answer sms] failed:", error?.message || res?.error || res?.skipped);
        return;
      }
      // Un-pause the bot so Sophia handles the reply, and stamp the guard.
      await supabase.from("leads").update({
        bot_paused: false,
        no_answer_sms_at: new Date().toISOString(),
        last_contacted_at: new Date().toISOString(),
      }).eq("id", leadId);
      await supabase.from("lead_activities").insert({
        lead_id: leadId,
        activity_type: "sms_sent",
        channel: "sms",
        direction: "outbound",
        content: "No-answer auto-text sent (Sophia will book if they reply)",
      });
    } catch (e) { console.warn("[no-answer sms] exception:", e); }
  };

  const markNoAnswer = async () => {
    // Fire the Sophia auto-text first — it should go out even on the 4th
    // attempt (last chance before the lead is parked as lost).
    await sendNoAnswerText();
    // 4-attempt callback cadence (owner-defined):
    //   Attempt 1 done → next call in 3h
    //   Attempt 2 done → next call in 24h
    //   Attempt 3 done → next call in 72h (3 days)
    //   Attempt 4 done → mark lost (cron will catch this too)
    const attemptsDone = (lead.call_attempts || 0);
    if (attemptsDone >= 4) {
      await updateStatus("lost", {
        lost_reason: "no_answer_after_4_attempts",
        status: "lost",
      });
      await supabase.from("lead_activities").insert({
        lead_id: leadId,
        activity_type: "no_answer",
        channel: "phone",
        direction: "outbound",
        content: "No answer on attempt 4 — moved to lost (long-term nurture)",
      });
      await load();
      setTimeout(onClose, 600);
      return;
    }
    const cadenceHours = attemptsDone === 1 ? 3 : attemptsDone === 2 ? 24 : 72;
    const followUpAt = new Date(Date.now() + cadenceHours * 60 * 60 * 1000);
    await updateStatus("callback_scheduled", {
      callback_scheduled_at: followUpAt.toISOString(),
      callback_note: `No answer on attempt ${attemptsDone} — retry in ${cadenceHours}h`,
    });
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "no_answer",
      channel: "phone",
      direction: "outbound",
      content: `No answer on attempt ${attemptsDone}/4. Next try: ${followUpAt.toLocaleString()}`,
    });
    await load();
  };

  const scheduleCallback = async (date, time, note) => {
    const dt = new Date(`${date}T${time}`);
    await updateStatus("callback_scheduled", {
      callback_scheduled_at: dt.toISOString(),
      callback_note: note,
    });
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "callback_scheduled",
      channel: "phone",
      content: `Callback scheduled for ${dt.toLocaleString()}: ${note || ""}`,
    });
    setShowCallback(false);
    await load();
  };

  const markLost = async (reason) => {
    await updateStatus("lost", { lost_reason: reason });
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "lost",
      channel: "system",
      content: `Marked as lost: ${reason}`,
    });
    setShowLost(false);
    await load();
    setTimeout(onClose, 500);
  };

  const copyPhone = () => {
    if (!lead?.phone) return;
    navigator.clipboard.writeText(lead.phone);
    setPhoneCopied(true);
    setTimeout(() => setPhoneCopied(false), 2000);
  };

  const sendQuickSMS = async (template) => {
    const message = template.content
      .replace(/\{\{first_name\}\}/g, lead.first_name || lead.full_name?.split(" ")[0] || "there")
      .replace(/\{\{full_name\}\}/g, lead.full_name || "")
      .replace(/\{\{book_link\}\}/g, "https://www.elyonhq.com/book")
      .replace(/\{\{callback_time\}\}/g, lead.callback_scheduled_at ? new Date(lead.callback_scheduled_at).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true}) : "soon");
    setSmsText(message);
    setShowQuickSMS(false);
  };

  const sendSMS = async () => {
    if (!smsText.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-lead-sms", {
        body: { lead_id: leadId, message: smsText },
      });
      // Even if function doesn't exist, log it as activity
      await supabase.from("lead_activities").insert({
        lead_id: leadId,
        activity_type: "sms_sent",
        channel: "sms",
        direction: "outbound",
        content: smsText,
      });
      await supabase.from("leads").update({ last_contacted_at: new Date().toISOString() }).eq("id", leadId);
      setSmsText("");
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await supabase.from("lead_activities").insert({
      lead_id: leadId,
      activity_type: "note_added",
      channel: "system",
      direction: "system",
      content: noteText,
    });
    setNoteText("");
    await load();
  };

  if (!lead) return null;

  const tempColor = lead.lead_temperature === "hot" ? C.hot : lead.lead_temperature === "cold" ? C.cold : C.warm;

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"flex-end",backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",width:"100%",maxWidth:580,height:"100vh",overflow:"auto",boxShadow:"-4px 0 24px rgba(0,0,0,0.2)"}}>
        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.border,background:"linear-gradient(135deg,"+C.goldBg+",rgba(180,140,60,0.04))",position:"sticky",top:0,zIndex:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                <span style={{fontSize:18}}>{lead.lead_temperature === "hot" ? "🔥" : "✨"}</span>
                <div style={{fontSize:20,fontWeight:800,color:C.text,fontFamily:S}}>{lead.full_name || "Anonymous"}</div>
              </div>
              <div style={{fontSize:11,color:C.muted}}>{lead.source} · {new Date(lead.created_at).toLocaleString()}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:22}}>×</button>
          </div>

          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <Pill color={tempColor}>{lead.lead_temperature?.toUpperCase()}</Pill>
            <Pill color={C.gold}>Score {lead.lead_score || 50}</Pill>
            {lead.estimated_value > 0 && <Pill color={C.green}>${lead.estimated_value}</Pill>}
            {lead.call_attempts > 0 && <Pill color={C.blue}>📞 {lead.call_attempts} attempts</Pill>}
          </div>
        </div>

        {/* Pipeline status indicator */}
        <div style={{padding:"12px 22px",background:C.light,borderBottom:"1px solid "+C.border}}>
          <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Current Status</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>{COLUMNS.find(c => c.id === lead.pipeline_status)?.title || lead.pipeline_status}</div>
          {lead.callback_note && <div style={{fontSize:11,color:C.muted,marginTop:4}}>📝 {lead.callback_note}</div>}
        </div>

        <div style={{padding:22}}>
          {/* CONTACT INFO */}
          <Section title="📱 Contact">
            {lead.phone && (
              <div style={{padding:"10px 14px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Phone</div>
                  <div style={{fontSize:18,fontWeight:700,color:C.text,fontFamily:"monospace"}}>{lead.phone}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={copyPhone} style={{padding:"8px 14px",background:phoneCopied?C.green:C.gold,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                    {phoneCopied ? "✓ Copied!" : "📋 Copy"}
                  </button>
                  <a href={`tel:${lead.phone}`} style={{padding:"8px 14px",background:C.green,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,fontFamily:F,textDecoration:"none",display:"inline-block"}}>📞 Call</a>
                </div>
              </div>
            )}
            {lead.email && (
              <div style={{padding:"8px 14px",background:C.light,borderRadius:8,marginBottom:8}}>
                <div style={{fontSize:10,color:C.muted}}>Email</div>
                <div style={{fontSize:13,color:C.text}}>{lead.email}</div>
              </div>
            )}
          </Section>

          {/* INTEREST */}
          {(lead.interest || lead.notes) && (
            <Section title="💭 What they want">
              {lead.interest && <div style={{padding:"10px 14px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,marginBottom:6,fontSize:13,fontWeight:600,color:C.text}}>{lead.interest}</div>}
              {lead.notes && <div style={{padding:"10px 14px",background:C.light,borderRadius:8,fontSize:12,color:C.muted}}>{lead.notes}</div>}
            </Section>
          )}

          {/* QUICK ACTIONS - context-aware buttons based on current status */}
          <Section title="⚡ Quick Actions">
            {/* ★ One-tap "I called" — the simplest way to mark a call. Turns the
                card green immediately; pick the outcome afterwards if you want. */}
            {!["booked","lost"].includes(lead.pipeline_status) && (
              <button onClick={markCalled} disabled={loading} style={{
                width:"100%", marginBottom:8, padding:"13px",
                background: loading ? C.light : "linear-gradient(135deg,"+C.blue+","+C.purple+")",
                color: loading ? C.muted : "#fff", border:"none", borderRadius:9,
                fontSize:14, fontWeight:800, cursor: loading?"not-allowed":"pointer", fontFamily:F,
              }}>
                📞 I Called This Lead{(lead.call_attempts||0) > 0 ? ` · ${lead.call_attempts} so far` : ""}
              </button>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
              {/* For any actionable lead (to_call / new / bot_chatting / callback_scheduled / calling / follow_up): show ALL outcome buttons directly so the worker can mark what happened in ONE click — no "Start Calling" → wait → mark required. */}
              {!["booked","lost"].includes(lead.pipeline_status) && (
                <>
                  <ActionBtn color={C.green} onClick={async () => { await startCalling(); await markBooked(); }} disabled={loading}>✅ Called — Booked</ActionBtn>
                  <ActionBtn color={C.warm}  onClick={async () => { await startCalling(); await markNoAnswer(); }} disabled={loading}>📵 Called — No Answer</ActionBtn>
                  <ActionBtn color="#a855f7" onClick={()=>setShowCallback(true)} disabled={loading}>⏰ Schedule Callback</ActionBtn>
                  <ActionBtn color={C.red}   onClick={()=>setShowLost(true)} disabled={loading}>❌ Lost / Not Interested</ActionBtn>
                </>
              )}
              {["booked","lost"].includes(lead.pipeline_status) && (
                <>
                  <ActionBtn color={C.green} onClick={markBooked} disabled={loading}>✅ Mark Booked</ActionBtn>
                  <ActionBtn color={C.red} onClick={()=>setShowLost(true)} disabled={loading}>❌ Mark Lost</ActionBtn>
                </>
              )}
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:4}}>
              💡 Tap <b>"I Called This Lead"</b> to log a call & turn the card green — then pick an outcome.
              "No Answer" auto-schedules the next attempt (3h → 24h → 72h, then Lost after 4 tries).
            </div>
          </Section>

          {/* SEND SMS */}
          <Section title="💬 Send SMS">
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <button onClick={()=>setShowQuickSMS(!showQuickSMS)} style={{padding:"6px 12px",background:C.blueL,color:C.blue,border:"1px solid "+C.blue+"40",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>
                ⚡ Quick Templates
              </button>
              <button onClick={()=>setSmsText("")} style={{padding:"6px 12px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:11,fontFamily:F,cursor:"pointer"}}>Clear</button>
            </div>
            {showQuickSMS && (
              <div style={{marginBottom:8,padding:8,background:C.light,borderRadius:8,maxHeight:200,overflow:"auto"}}>
                {smsTemplates.map(t => (
                  <button key={t.id} onClick={()=>sendQuickSMS(t)} style={{display:"block",width:"100%",padding:"8px 10px",marginBottom:4,background:"#fff",border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",textAlign:"left",fontFamily:F}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.text}}>{t.name}</div>
                    <div style={{fontSize:10,color:C.muted,marginTop:2}}>{t.content.substring(0,80)}...</div>
                  </button>
                ))}
              </div>
            )}
            <textarea value={smsText} onChange={e=>setSmsText(e.target.value)} rows={3} placeholder="Type your message..." style={{...inp,resize:"vertical",fontFamily:F}}/>
            <button onClick={sendSMS} disabled={loading || !smsText.trim() || !lead.phone} style={{marginTop:6,padding:"8px 14px",background:loading?C.light:"linear-gradient(135deg,"+C.blue+","+C.purple+")",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:F,width:"100%"}}>
              {loading ? "Sending..." : `📤 Send SMS to ${lead.phone || "—"}`}
            </button>
          </Section>

          {/* ADD NOTE */}
          <Section title="📝 Add Note">
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} rows={2} placeholder="e.g. Said she'll call back tomorrow at 2pm..." style={{...inp,resize:"vertical"}}/>
            <button onClick={addNote} disabled={!noteText.trim()} style={{marginTop:6,padding:"7px 14px",background:noteText.trim()?C.gold:C.light,color:noteText.trim()?"#fff":C.muted,border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:noteText.trim()?"pointer":"not-allowed",fontFamily:F,width:"100%"}}>
              Save Note
            </button>
          </Section>

          {/* ACTIVITY TIMELINE */}
          <Section title="📜 Activity Timeline">
            {activities.length === 0 ? (
              <div style={{padding:14,textAlign:"center",color:C.muted,fontSize:11}}>No activity yet</div>
            ) : (
              <div style={{maxHeight:300,overflow:"auto"}}>
                {activities.map(a => (
                  <ActivityItem key={a.id} activity={a}/>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Schedule Callback Modal */}
        {showCallback && <ScheduleCallback onSchedule={scheduleCallback} onClose={()=>setShowCallback(false)}/>}

        {/* Lost Reason Modal */}
        {showLost && <LostReasonModal onSelect={markLost} onClose={()=>setShowLost(false)}/>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ScheduleCallback({ onSchedule, onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("15:00");
  const [note, setNote] = useState("");

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:380,width:"100%",padding:20}}>
        <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:14}}>⏰ Schedule Callback</div>
        <div style={{marginBottom:10}}>
          <Label>Date</Label>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} min={today} style={inp}/>
        </div>
        <div style={{marginBottom:10}}>
          <Label>Time</Label>
          <input type="time" value={time} onChange={e=>setTime(e.target.value)} style={inp}/>
        </div>
        <div style={{marginBottom:14}}>
          <Label>Note (optional)</Label>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. She wanted to discuss with husband first" style={inp}/>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{padding:"8px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={()=>onSchedule(date, time, note)} style={{padding:"8px 18px",background:"linear-gradient(135deg,#a855f7,#7c3aed)",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Schedule</button>
        </div>
      </div>
    </div>
  );
}

function LostReasonModal({ onSelect, onClose }) {
  const reasons = [
    "Not interested", "Too expensive", "Not ready yet",
    "Going elsewhere", "Wrong number", "Wrong service",
    "No response after 3+ tries", "Other"
  ];

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:380,width:"100%",padding:20}}>
        <div style={{fontSize:16,fontWeight:800,color:C.red,fontFamily:S,marginBottom:14}}>❌ Mark as Lost - Why?</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {reasons.map(r => (
            <button key={r} onClick={()=>onSelect(r)} style={{padding:"10px 14px",background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:7,fontSize:13,cursor:"pointer",fontFamily:F,textAlign:"left"}}
              onMouseEnter={e=>e.currentTarget.style.background=C.redL}
              onMouseLeave={e=>e.currentTarget.style.background=C.light}>
              {r}
            </button>
          ))}
        </div>
        <button onClick={onClose} style={{marginTop:12,width:"100%",padding:"8px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ActivityItem({ activity }) {
  const icons = {
    created: "✨", re_engaged: "🔄", sms_sent: "📱", sms_replied: "💬", email_sent: "📧",
    email_opened: "👁", call_started: "📞", call_completed: "✅", no_answer: "📵",
    callback_scheduled: "⏰", callback_due: "⏰", booked: "🎉", lost: "❌",
    note_added: "📝", status_changed: "🔄", auto_progressed: "🤖", sms_skipped: "⚠️",
  };
  const icon = icons[activity.activity_type] || "•";
  const time = new Date(activity.created_at);
  return (
    <div style={{padding:"8px 10px",borderLeft:"3px solid "+C.borderGold,marginBottom:6,background:C.light,borderRadius:6}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
        <span style={{fontWeight:700,color:C.text}}>{icon} {activity.activity_type.replace(/_/g," ")}</span>
        <span style={{color:C.dim,fontSize:10}}>{time.toLocaleString()}</span>
      </div>
      {activity.content && <div style={{fontSize:11,color:C.muted,whiteSpace:"pre-wrap"}}>{activity.content.substring(0,200)}{activity.content.length > 200 ? "..." : ""}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────
function Stat({ label, value, color }) {
  return (
    <div style={{padding:11,background:C.card,border:"1px solid "+C.border,borderRadius:9,borderLeft:"3px solid "+color}}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{label}</div>
      <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S}}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:16}}>
      <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{title}</div>
      {children}
    </div>
  );
}

function Pill({ color, children }) {
  return <span style={{padding:"3px 10px",background:color+"20",color:color,borderRadius:50,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{children}</span>;
}

function Label({ children }) {
  return <label style={{display:"block",fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{children}</label>;
}

function ActionBtn({ color, onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:"10px 12px",
      background:disabled ? C.light : "linear-gradient(135deg,"+color+","+color+"dd)",
      color:disabled?C.muted:"#fff",
      border:"none",
      borderRadius:8,
      fontSize:12,
      fontWeight:700,
      cursor:disabled?"not-allowed":"pointer",
      fontFamily:F,
      whiteSpace:"nowrap",
    }}>
      {children}
    </button>
  );
}
